import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
  apiBase: string;
  onVerified: (email: string, emailToken: string) => void;
};

const RESEND_COOLDOWN = 60;

export default function PreRegEmailVerify({ apiBase, onVerified }: Props) {
  const { t, i18n } = useTranslation();
  const [phase, setPhase] = useState<'enter-email' | 'enter-code'>('enter-email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = setInterval(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearInterval(id);
  }, [resendCooldown]);

  const requestCode = async () => {
    setError('');
    setInfo('');
    if (!email || !email.includes('@')) {
      setError(t('preReg.invalidEmail', 'Email invalide'));
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/auth/pre-register/email/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase(), lang: i18n.language === 'ar' ? 'ar' : 'fr' }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || t('preReg.sendError', "Erreur d'envoi"));
        return;
      }
      setPhase('enter-code');
      setInfo(t('preReg.emailCodeSent', 'Code envoyé à {{email}}.', { email }));
      setResendCooldown(RESEND_COOLDOWN);
    } catch {
      setError(t('preReg.sendError', "Erreur d'envoi"));
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async () => {
    setError('');
    if (code.length !== 6) {
      setError(t('preReg.invalidCodeLength', 'Le code doit faire 6 chiffres.'));
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/auth/pre-register/email/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase(), code }),
      });
      const data = await res.json();
      if (!res.ok || !data.email_token) {
        setError(data.detail || t('preReg.invalidCode', 'Code invalide.'));
        return;
      }
      onVerified(email.trim().toLowerCase(), data.email_token);
    } catch {
      setError(t('preReg.invalidCode', 'Code invalide.'));
    } finally {
      setLoading(false);
    }
  };

  const handleEnter = (action: () => void) => (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') action();
  };

  return (
    <div className="prereg-step">
      <h4 className="mb-3">{t('preReg.emailTitle', 'Vérifiez votre email')}</h4>

      {error && (
        <div className="alert alert-danger" role="alert">
          <i className="bi bi-exclamation-triangle me-2"></i>{error}
        </div>
      )}
      {info && !error && (
        <div className="alert alert-info" role="alert">
          <i className="bi bi-info-circle me-2"></i>{info}
        </div>
      )}

      <div className="mb-3">
        <label className="form-label">{t('common.email', 'Email')} <span className="text-danger">*</span></label>
        <div className="input-group">
          <span className="input-group-text"><i className="bi bi-envelope"></i></span>
          <input
            type="email"
            className="form-control"
            placeholder="exemple@email.com"
            value={email}
            disabled={phase === 'enter-code'}
            onChange={(e) => setEmail(e.target.value)}
            onKeyPress={handleEnter(requestCode)}
          />
        </div>
      </div>

      {phase === 'enter-email' && (
        <button onClick={requestCode} className="btn btn-primary w-100" disabled={loading}>
          {loading
            ? <><span className="spinner-border spinner-border-sm me-2"></span>{t('common.loading', 'Chargement...')}</>
            : <><i className="bi bi-envelope-arrow-up me-2"></i>{t('preReg.sendCode', 'Envoyer le code')}</>}
        </button>
      )}

      {phase === 'enter-code' && (
        <>
          <div className="mb-3">
            <label className="form-label">{t('preReg.codeLabel', 'Code reçu par email')}</label>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              className="form-control text-center"
              style={{ fontSize: '24px', letterSpacing: '8px' }}
              placeholder="——————"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              onKeyPress={handleEnter(verifyCode)}
              autoFocus
            />
          </div>

          <button onClick={verifyCode} className="btn btn-primary w-100 mb-2" disabled={loading}>
            {loading
              ? <><span className="spinner-border spinner-border-sm me-2"></span>{t('common.loading', 'Chargement...')}</>
              : <><i className="bi bi-check2-circle me-2"></i>{t('preReg.verify', 'Vérifier')}</>}
          </button>

          <button
            onClick={requestCode}
            className="btn btn-link w-100"
            disabled={loading || resendCooldown > 0}
          >
            {resendCooldown > 0
              ? t('preReg.resendIn', 'Renvoyer dans {{s}}s', { s: resendCooldown })
              : t('preReg.resend', 'Renvoyer le code')}
          </button>
        </>
      )}
    </div>
  );
}
